/**
 * Safe wrapper around the Web Storage API (localStorage / sessionStorage).
 *
 * Some environments expose a `null` storage object, or throw when storage is
 * touched — most notably Android WebViews with DOM storage disabled (the
 * platform default), and iOS WKWebViews / iframes where the host app blocks
 * storage (e.g. Teachino embedded in the Untis app). Reading
 * `localStorage.getItem(...)` there throws
 * `Cannot read properties of null (reading 'getItem')` and, because this runs
 * during app boot (Pinia session store init, urql plugin), it crashes the whole
 * app and the user only sees the generic error page.
 *
 * This module never touches the real storage object directly. It probes once for
 * a usable backend and, if none is available, transparently falls back to an
 * in-memory store. The app then keeps working for the lifetime of the
 * tab/iframe (login, token refresh, etc.) — it just won't persist across
 * reloads, which is far better than a hard crash.
 *
 * Use {@link safeLocalStorage} / {@link safeSessionStorage} everywhere instead
 * of the global `localStorage` / `sessionStorage`.
 */

type StorageKind = 'local' | 'session';

export type SafeStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
  /** `true` when backed by real Web Storage, `false` for the in-memory fallback. */
  readonly isPersistent: boolean;
};

/**
 * Window flag to force the in-memory fallback for manual testing.
 * Must be set before the first storage access (i.e. before app boot).
 */
const SIMULATE_DISABLED_FLAG = '__TEACHINO_DISABLE_STORAGE__';

/**
 * Query params that force the in-memory fallback. Reliable inside iframes
 * because it is read synchronously at boot. See `/iframe-test.html`.
 */
const SIMULATE_QUERY_PARAMS = ['noStorage', 'disableStorage'];

const PROBE_KEY = '__teachino_storage_probe__';

const warned = new Set<StorageKind>();

function warnFallbackOnce(kind: StorageKind, simulated: boolean): void {
  if (warned.has(kind)) {
    return;
  }
  warned.add(kind);
  console.warn(
    `[safe-storage] ${kind}Storage ${
      simulated ? 'disabled via simulation flag' : 'is unavailable'
    }; falling back to in-memory storage. Data will not persist across reloads.`,
  );
}

function shouldSimulateUnavailable(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  if (
    (window as unknown as Record<string, unknown>)[SIMULATE_DISABLED_FLAG] ===
    true
  ) {
    return true;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    return SIMULATE_QUERY_PARAMS.some((param) => params.has(param));
  } catch {
    return false;
  }
}

function getNativeStorage(kind: StorageKind): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    // Typed as nullable on purpose: in restricted WebViews the property itself
    // can be `null`/`undefined`, even though the DOM lib types it as non-null.
    const storage = (
      kind === 'local' ? window.localStorage : window.sessionStorage
    ) as Storage | null;
    if (!storage) {
      return null;
    }
    // Some browsers expose the object but throw on access (private mode, quota,
    // blocked third-party storage). Probe with a real write/remove cycle.
    storage.setItem(PROBE_KEY, '1');
    storage.removeItem(PROBE_KEY);
    return storage;
  } catch {
    return null;
  }
}

function createMemoryStorage(): SafeStorage {
  const store = new Map<string, string>();
  return {
    getItem: (key) => (store.has(key) ? (store.get(key) as string) : null),
    setItem: (key, value) => {
      store.set(key, value);
    },
    removeItem: (key) => {
      store.delete(key);
    },
    clear: () => {
      store.clear();
    },
    isPersistent: false,
  };
}

function wrapNativeStorage(storage: Storage): SafeStorage {
  return {
    getItem(key) {
      try {
        return storage.getItem(key);
      } catch {
        return null;
      }
    },
    setItem(key, value) {
      try {
        storage.setItem(key, value);
      } catch {
        // Swallow runtime failures (e.g. quota exceeded) so callers never crash.
      }
    },
    removeItem(key) {
      try {
        storage.removeItem(key);
      } catch {
        // ignore
      }
    },
    clear() {
      try {
        storage.clear();
      } catch {
        // ignore
      }
    },
    isPersistent: true,
  };
}

function createSafeStorage(kind: StorageKind): SafeStorage {
  let backend: SafeStorage | null = null;

  function resolve(): SafeStorage {
    if (backend) {
      return backend;
    }
    if (shouldSimulateUnavailable()) {
      warnFallbackOnce(kind, true);
      backend = createMemoryStorage();
      return backend;
    }
    const native = getNativeStorage(kind);
    if (native) {
      backend = wrapNativeStorage(native);
      return backend;
    }
    warnFallbackOnce(kind, false);
    backend = createMemoryStorage();
    return backend;
  }

  return {
    getItem: (key) => resolve().getItem(key),
    setItem: (key, value) => resolve().setItem(key, value),
    removeItem: (key) => resolve().removeItem(key),
    clear: () => resolve().clear(),
    get isPersistent() {
      return resolve().isPersistent;
    },
  };
}

export const safeLocalStorage: SafeStorage = createSafeStorage('local');
export const safeSessionStorage: SafeStorage = createSafeStorage('session');

/**
 * Key under which {@link safeLocalStorage} is published on `globalThis`.
 *
 * This lets modules that cannot import this file without creating a dependency
 * cycle share the exact same backing store (including the in-memory fallback).
 * Concretely: `@teachino/shared-util` already depends on `@teachino/shared-i18n`,
 * so `i18n.ts` cannot import from here — it reads this global instead. Keep the
 * literal in sync with the reader in `libs/shared/i18n/src/i18n.ts`.
 */
export const GLOBAL_SAFE_LOCAL_STORAGE_KEY = '__teachinoSafeLocalStorage__';

if (typeof globalThis !== 'undefined') {
  (globalThis as unknown as Record<string, SafeStorage>)[
    GLOBAL_SAFE_LOCAL_STORAGE_KEY
  ] = safeLocalStorage;
}

/** `true` when localStorage is real and persistent (not the in-memory fallback). */
export function isLocalStorageAvailable(): boolean {
  return safeLocalStorage.isPersistent;
}

/** `true` when sessionStorage is real and persistent (not the in-memory fallback). */
export function isSessionStorageAvailable(): boolean {
  return safeSessionStorage.isPersistent;
}
